import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Question } from './question';

@Injectable({ providedIn: 'root' })
export class ListReorderService {
  private renderer: Renderer2;

  constructor(rendererFactory: RendererFactory2) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  public  sortBy:string = 'votes';

public performSortAnimation(oldList: Question[], newList: Question[]): Question[] {
  let sortedList: Question[] = [];
  if (this.sortBy === 'votes') {
    sortedList = this.sortByVotes(newList);
  } else if(this.sortBy ==='date') {
    sortedList = this.sortByTimestampAsc(newList);
  }else{
    sortedList = this.sortByTimestampDes(newList);
  }

  if (oldList.length === 0 || newList.length !== oldList.length) {
    return sortedList;
  }
  const oldIds = oldList.map(q => q.id);
  const newIds = sortedList.map(q => q.id);

  const orderUnchanged = oldIds.every((id, idx) => id === newIds[idx]);
  const stickyUnchanged = oldList.every((q, idx) => q.sticky_enabled === sortedList[idx].sticky_enabled);

  if ((orderUnchanged && stickyUnchanged) || oldList.length === 0 || newList.length !== oldList.length) {
    return sortedList;
  }

  const commonIds = oldList.map(q => q.id).filter(id => sortedList.some(q => q.id === id));

  const itemElements = commonIds
    .map(id => document.getElementById(String(id)))
    .filter((el): el is HTMLElement => !!el);

  if (itemElements.length < 1) return sortedList;

  const itemPositions = itemElements.map(el => el.getBoundingClientRect());

  const parent = itemElements[0]?.parentElement;
  if (!parent) return sortedList;

  sortedList.forEach(q => {
    if (commonIds.includes(q.id)) {
      const el = document.getElementById(String(q.id));
      if (el) this.renderer.appendChild(parent, el);
    }
  });

  const newElements = commonIds
    .map(id => document.getElementById(String(id)))
    .filter((el): el is HTMLElement => !!el);

  const newPositions = newElements.map(el => el.getBoundingClientRect());
  newElements.forEach((el, index) => {
    const oldIndex = oldList.findIndex(q => q.id === commonIds[index]);
    if (oldIndex === -1) return;

    const deltaX = itemPositions[oldIndex].left - newPositions[index].left;
    const deltaY = itemPositions[oldIndex].top - newPositions[index].top;

    this.renderer.setStyle(el, 'transform', `translate(${deltaX}px, ${deltaY}px)`);
    this.renderer.setStyle(el, 'transition', 'transform 0s, box-shadow 0.4s ease-in-out, filter 0.4s ease,background-color 0.4s ease');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Move-Animation starten, immer ohne Delay!
        this.renderer.setStyle(el, 'transform', '');
        this.renderer.setStyle(
          el,
          'transition',
          'transform 1200ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 500ms, box-shadow 0.4s ease-in-out, background-color 0.4s ease ,filter 0.4s ease'
        );

        // Hintergrundfarbe für sticky_enabled auf .voting_item anwenden oder entfernen, inkl. Transition
        const q = sortedList.find(q => q.id === commonIds[index]);
        const votingItem = el.querySelector('.voting_item') as HTMLElement | null;
        if (votingItem) {
          this.renderer.setStyle(
            votingItem,
            'transition',
            'filter 0.4s ease, background-color 0.4s ease'
          );
          if (q?.sticky_enabled && this.sortBy === 'votes') {
            this.renderer.addClass(votingItem,'sticky')
          } else {
             this.renderer.removeClass(votingItem,'sticky')
          }
        }

        // Highlight nur, wenn nach vorne bewegt
        const sortedIndex = sortedList.findIndex(q => q.id === commonIds[index]);
        if (sortedIndex < oldIndex) {
          this.renderer.setStyle(
            el,
            'box-shadow',
            '0 0 10px rgba(255, 215, 0, 0.8), 0 0 20px rgba(255, 215, 0, 0.6)'
          );
          this.renderer.setStyle(el, 'z-index', '100');
          this.renderer.setStyle(el, 'position', 'relative');
          setTimeout(() => {
            this.renderer.setStyle(
              el,
              'box-shadow',
              '0 0 10px rgba(255, 215, 0, 0), 0 0 20px rgba(255, 215, 0, 0)'
            );
            setTimeout(() => {
              this.renderer.removeStyle(el, 'box-shadow');
              this.renderer.removeStyle(el, 'z-index');
              this.renderer.removeStyle(el, 'position');
            }, 500);
          }, 1700);
        }
      });
    });
  });

  return sortedList;
}

  public sortByVotes(questions: Question[]): Question[] {
    return this.sortByTimestampDesc(questions).slice().sort((a, b) => {
      if (a.sticky_enabled && b.sticky_enabled) {
        return (
          (new Date(a.timestamp_sticky).getTime() || 0) -
          (new Date(b.timestamp_sticky).getTime() || 0)
        );
      }
      if (a.sticky_enabled && !b.sticky_enabled) return -1;
      if (!a.sticky_enabled && b.sticky_enabled) return 1;

      return (b.votes || 0) - (a.votes || 0);
    });
  }

  public sortByTimestampAsc(questions: Question[]): Question[] {
    return questions.slice().sort((a, b) => {
      return (new Date(b?.date_of_approval).getTime() || 0) - (new Date(a?.date_of_approval).getTime() || 0);
    });
  }
  public sortByTimestampDes(questions: Question[]): Question[] {
    return questions.slice().sort((a, b) => {
      return (new Date(a?.date_of_approval).getTime() || 0) - (new Date(b?.date_of_approval).getTime() || 0);
    });
  }
  
  public sortByTimestampDesc(questions: Question[]): Question[] {
    return questions.slice().sort((a, b) => {
      return (new Date(a?.date_of_approval).getTime() || 0) - (new Date(b?.date_of_approval).getTime() || 0);
    });
  }

public removeAllStickyFilters(questions: Question[]): void {
  questions.forEach(q => {
    const el = document.getElementById(String(q.id));
    if (el) {
      const votingItem = el.querySelector('.voting_item') as HTMLElement | null;
      if (votingItem) {
         this.renderer.removeClass(votingItem,'sticky')
        this.renderer.removeStyle(votingItem, 'transition');
      }
    }
  });
}

public highlightStickyElements(questions: Question[]): void {
  if(questions && questions.length > 0){
  questions.forEach(q => {
    const el = document.getElementById(String(q.id));
    if (el) {
      const votingItem = el.querySelector('.voting_item') as HTMLElement | null;
      if (votingItem && q.sticky_enabled) {
        this.renderer.setStyle(votingItem, 'transition', 'filter 0.4s ease, background-color 0.4s ease');
        this.renderer.addClass(votingItem,'sticky')
      }
    }
  });
  }
}
}
